import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { ICustomer } from "../../../../common/data/dtos/customer";
import { IService } from "../../../../common/data/dtos/service";
import { IServicePrice } from "../../../../common/data/dtos/service-price";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Select from "../../../../components/bootstrap/forms/Select";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import servicePriceService from "../../../../services/service-price-service";

type ServicePriceFormValues = {
    currentPrice: number;
    newPrice: number;
    customerId: string;
};

interface Props {
    isModalOpened: boolean;
    selectedService: IService | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
    customers: ICustomer[];
}

export const ServicePriceModal = ({ isModalOpened, selectedService, onModalClose, onAfterSubmit, customers }: Props) => {
    const formik = useFormik({
        initialValues: {
            currentPrice: 0,
            customerId: '',
            newPrice: 0,
        },
        validate: (values) => {
            const errors: {
                newPrice?: string;
                customerId?: string;
            } = {};

            if (!values.newPrice) {
                errors.newPrice = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            if (!values.customerId) {
                errors.customerId = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [servicePrice, setServicePrice] = useState<IServicePrice | null>();

    useEffect(() => {
        //fetchServicePrice();
        resetForm();
    }, [isModalOpened]);

    const fetchServicePrice = async (customerId: string) => {

        if (!selectedService || !selectedService.id || !customerId) {
            formik.setFieldValue("customerId", '');
            return;
        }

        formik.setFieldValue("customerId", customerId);

        setIsSubmitting(true);

        const response = await servicePriceService.getActiveByService(selectedService?.id, customerId);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        setServicePrice(response.servicePrice);

        if (response.servicePrice)
            setFieldValues(response.servicePrice);
    };

    const onFormSubmit = async (values: ServicePriceFormValues) => {

        setIsSubmitting(true);

        const response = await servicePriceService.createServicePrice({
            servicePriceToInactivate: servicePrice && servicePrice.id ? servicePrice.id : undefined,
            price: values.newPrice,
            serviceId: selectedService?.id,
            customerId: values?.customerId
        });

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                currentPrice: 0,
                newPrice: 0,
                customerId: ''
            },
        });
    };

    const setFieldValues = (values: IServicePrice) => {
        formik.setFieldValue('currentPrice', values.price || 0);
        formik.setFieldValue('newPrice', 0);
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='service-price-modal'>Manutenção de preços</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados do preço' icon='AttachMoney'>
                        <div className='row g-4'>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='customerId' label='Cliente' isFloating>
                                    <Select
                                        ariaLabel='Escolha um cliente'
                                        list={[
                                            { value: '', text: 'Selecione um cliente' },
                                            ...customers.map((customer) => ({
                                                value: customer.id,
                                                text: `${customer.name}`
                                            }))
                                        ]}
                                        onChange={(e: { target: { value: any } }) => {
                                            fetchServicePrice(e.target.value);
                                        }}
                                        value={formik.values.customerId}
                                        onBlur={formik.handleBlur}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.customerId}
                                        invalidFeedback={formik.errors.customerId}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='currentPrice' label='Valor Atual' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.currentPrice}
                                        isTouched={formik.touched.currentPrice}
                                        disabled={true}
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='newPrice' label='Novo Preço' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.newPrice}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.newPrice}
                                        invalidFeedback={formik.errors.newPrice}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};